/**
 * router/index.ts
 *
 * Automatic routes for `./src/pages/*.vue`
 */

// Composables
import { createRouter, createWebHistory } from 'vue-router/auto'
import { routes } from 'vue-router/auto-routes'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'Home',
      component: () => import('@/pages/index.vue'),
      meta: {
        title: 'Hockeybook',
        description: 'Hokejový manažer je platforma pro všechny amatérské hokejisty a hokejové nadšence.',
      },
    },
    {
      // Krátká URL pro SMS pozvánky - redirect na registraci s tokenem
      path: '/i/:token',
      name: 'InviteShortcut',
      redirect: to => ({
        path: '/register',
        query: { invite: to.params.token }
      }),
    },
    {
      path: '/messages',
      name: 'Chat',
      component: () => import('@/pages/messages/index.vue'),
      meta: {
        title: 'Chat',
      },
    },
    {
      path: '/messages/:id',
      name: 'ChatConversation',
      component: () => import('@/pages/messages/index.vue'),
      meta: {
        title: 'Chat',
      },
    },
    ...routes,  // Automatické routy z `pages`
    {
      //path: '/users/:userId-:firstName-:lastName',  // Dynamická route pro uživatelský profil
      path: '/users/:userId',  // Dynamická route pro uživatelský profil
      name: 'UserProfile',
      component: () => import('@/pages/users/userProfile.vue'),  // Ujisti se, že máš tento soubor ve složce `pages`
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // zpět/vpřed
    if (savedPosition) return savedPosition;

    // anchor (hash) – bez ruční manipulace s DOM
    if (to.hash) {
      return {
        el: to.hash,         // např. #section-2
        behavior: 'smooth',
      };
    }

    // default
    return { left: 0, top: 0 };
  },
})

// Workaround for https://github.com/vitejs/vite/issues/11804
// A pro obecné chyby při dynamickém importu (chunky se změní po deployi)
router.onError((err, to) => {
  const isChunkError = 
    err?.message?.includes?.('Failed to fetch dynamically imported module') ||
    err?.message?.includes?.('Loading chunk') ||
    err?.message?.includes?.('Loading CSS chunk') ||
    err?.message?.includes?.('Unable to preload CSS') ||
    err?.name === 'ChunkLoadError'
  
  if (isChunkError) {
    const reloadKey = 'vuetify:dynamic-reload'
    const lastReload = localStorage.getItem(reloadKey)
    const now = Date.now()
    
    // Povol reload pouze jednou za 10 sekund, aby se zabránilo infinite loop
    if (!lastReload || (now - parseInt(lastReload, 10)) > 10000) {
      console.warn('Chunk loading error, reloading page...', err?.message)
      localStorage.setItem(reloadKey, now.toString())
      // Použij location.href místo assign pro čistší reload
      window.location.href = to.fullPath
    } else {
      console.error('Chunk loading error persists after reload:', err)
      // Vyčisti flag po 10 sekundách
      setTimeout(() => localStorage.removeItem(reloadKey), 10000)
    }
  } else {
    console.error('Router error:', err)
  }
})


// Přidání beforeEach pro nastavení meta tagů
router.beforeEach((to, from, next) => {
  // Nastavení title
  const nearestWithTitle = to.matched.slice().reverse().find(r => r.meta && r.meta.title);
  if (nearestWithTitle) {
    // Type assertion to ensure TypeScript treats it as string
    document.title = nearestWithTitle.meta.title as string;
  }

  // Nastavení meta description
  const nearestWithMeta = to.matched.slice().reverse().find(r => r.meta && r.meta.description);
  if (nearestWithMeta) {
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      // Type assertion to ensure TypeScript treats it as string
      metaDescription.setAttribute('content', nearestWithMeta.meta.description as string);
    }
  }
  next();
});



router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload')
})



export default router
